import React, { createContext, useMemo } from "react";

import { useListDevices } from "#src/components/hooks/adapters/useDevices";
import { useListEquipment } from "#src/components/hooks/adapters/useEquipment";
import { useGetOneEvent } from "#src/components/hooks/adapters/useEvents";
import { useListFacilities } from "#src/components/hooks/adapters/useFacilities";
import { useListFormSubmissions } from "#src/components/hooks/adapters/useFormSubmissions";
import type {
  DeviceType,
  EquipmentType,
  EventType,
  FacilityType,
  FormSubmissionType,
} from "@validereinc/domain";

type EnrichedEventDetailsType = EventType & {
  form_submissions: {
    data: FormSubmissionType[];
    total: number;
  };
  equipment: {
    data: EquipmentType[];
    total: number;
  };
  devices: {
    data: DeviceType[];
    total: number;
  };
  alerts: {
    data: never[];
    total: number;
  };
  facility?: FacilityType;
};

type EventContextType = {
  event?: EnrichedEventDetailsType;
  isLoading: boolean;
  onRefetchEvent: () => void;
};

export const EventContext = createContext<EventContextType | null>(null);

export const EventProvider = ({
  eventId = "",
  children,
}: {
  eventId: string;
  children: React.ReactElement | React.ReactElement[];
}) => {
  const enabled = Boolean(eventId);
  const filters = { "event.id": eventId };

  const eventQuery = useGetOneEvent({ id: eventId }, { enabled });
  const event = eventQuery.data?.data;

  const formSubmissionQuery = useListFormSubmissions(
    {
      filters,
      meta: {
        answers: true,
        omitSchema: true,
      },
    },
    {
      enabled,
    }
  );
  const formSubmissions = formSubmissionQuery.data?.data;

  const facilitiesQuery = useListFacilities({ filters }, { enabled });
  const facility = facilitiesQuery.data?.data[0];

  const equipmentQuery = useListEquipment({ filters }, { enabled });
  const equipment = equipmentQuery.data?.data;

  const devicesQuery = useListDevices({ filters }, { enabled });
  const devices = devicesQuery.data?.data;

  const isLoading =
    enabled &&
    (eventQuery.isLoading ||
      formSubmissionQuery.isLoading ||
      equipmentQuery.isLoading ||
      devicesQuery.isLoading ||
      facilitiesQuery.isLoading);

  const value = useMemo<EventContextType>(
    () => ({
      event: event
        ? {
            ...event,
            alerts: {
              total: 0,
              data: [],
            },
            form_submissions: {
              total: formSubmissions?.length ?? 0,
              data: formSubmissions ?? [],
            },
            equipment: {
              total: equipment?.length ?? 0,
              data: equipment ?? [],
            },
            devices: {
              total: devices?.length ?? 0,
              data: devices ?? [],
            },
            facility,
          }
        : undefined,
      isLoading,
      onRefetchEvent: () => {
        eventQuery.refetch();
        formSubmissionQuery.refetch();
        facilitiesQuery.refetch();
        equipmentQuery.refetch();
        devicesQuery.refetch();
      },
    }),
    [event, formSubmissions, equipment, devices, facility, isLoading]
  );

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};
